import { useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Download, Calendar, Users, ChevronsUpDown, Check } from "lucide-react";
import { toast } from "sonner";

const toISO = (d) => d.toISOString().slice(0, 10);
const today = () => toISO(new Date());

function startOfWeekMon(iso) {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7; // 0=Mon
  d.setDate(d.getDate() - dow);
  return toISO(d);
}
function addDays(iso, n) {
  const d = new Date(iso); d.setDate(d.getDate() + n); return toISO(d);
}
function startOfMonth(iso) { const d = new Date(iso); d.setDate(1); return toISO(d); }
function endOfMonth(iso) { const d = new Date(iso); d.setMonth(d.getMonth() + 1); d.setDate(0); return toISO(d); }

export default function ReportJobs() {
  const [period, setPeriod] = useState("weekly");
  const [date, setDate] = useState(today());
  // Custom-range state — only used when period === "custom".
  const [customFrom, setCustomFrom] = useState(today());
  const [customTo, setCustomTo]     = useState(today());
  const [clientId, setClientId] = useState("__all");
  const [siteId, setSiteId] = useState("__all");
  const [sites, setSites] = useState([]);
  const [includeSubs, setIncludeSubs] = useState(true);   // toggle: show "Sub / Driver" columns in the PDF
  const [clients, setClients] = useState([]);

  // Contact coverage state
  const [contacts, setContacts] = useState([]);
  const [contactKind, setContactKind] = useState("sub");          // sub | self
  const [contactId, setContactId] = useState("");
  const [cPeriod, setCPeriod] = useState("weekly");
  const [cDate, setCDate] = useState(today());
  const [cCustomFrom, setCCustomFrom] = useState(today());
  const [cCustomTo,   setCCustomTo]   = useState(today());
  const [includeClients, setIncludeClients] = useState(true);     // toggle: show "Client" column in the coverage PDF
  const [covClientId, setCovClientId] = useState("__all");        // optional client filter — "__all" = every client

  // Contact KPI state — same shape as coverage but its own filters
  const [kContactKind, setKContactKind] = useState("sub");
  const [kContactId, setKContactId] = useState("");
  const [kPeriod, setKPeriod] = useState("monthly");
  const [kDate, setKDate] = useState(today());
  const [kCustomFrom, setKCustomFrom] = useState(today());
  const [kCustomTo,   setKCustomTo]   = useState(today());

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data || [])).catch(e => toast.error(formatApiError(e)));
    api.get("/contacts").then(r => setContacts(r.data || [])).catch(e => toast.error(formatApiError(e)));
  }, []);

  // Fetch sites whenever the picked client changes so the Site dropdown is
  // populated. Reset the site selection whenever the client changes.
  useEffect(() => {
    setSiteId("__all");
    if (!clientId || clientId === "__all") { setSites([]); return; }
    api.get(`/clients/${clientId}/sites`)
       .then(r => setSites(r.data || []))
       .catch(() => setSites([]));
  }, [clientId]);

  const { dateFrom, dateTo, label } = useMemo(() => {
    if (period === "custom") {
      const from = customFrom || customTo || today();
      const to   = customTo   || customFrom || today();
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      return { dateFrom: lo, dateTo: hi, label: `${lo} → ${hi}` };
    }
    if (period === "daily") return { dateFrom: date, dateTo: date, label: date };
    if (period === "weekly") {
      const m = startOfWeekMon(date);
      const s = addDays(m, 6);
      return { dateFrom: m, dateTo: s, label: `${m} → ${s}` };
    }
    const m = startOfMonth(date); const e = endOfMonth(date);
    return { dateFrom: m, dateTo: e, label: `${m} → ${e}` };
  }, [period, date, customFrom, customTo]);

  const cRange = useMemo(() => {
    if (cPeriod === "custom") {
      const from = cCustomFrom || cCustomTo || today();
      const to   = cCustomTo   || cCustomFrom || today();
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      return { from: lo, to: hi, label: `${lo} → ${hi}` };
    }
    if (cPeriod === "daily") return { from: cDate, to: cDate, label: cDate };
    if (cPeriod === "weekly") {
      const m = startOfWeekMon(cDate); const s = addDays(m, 6);
      return { from: m, to: s, label: `${m} → ${s}` };
    }
    const m = startOfMonth(cDate); const e = endOfMonth(cDate);
    return { from: m, to: e, label: `${m} → ${e}` };
  }, [cPeriod, cDate, cCustomFrom, cCustomTo]);

  const filteredContacts = useMemo(() => {
    // Codebase convention: type === "customer" is a subcontractor;
    //                       type === "subcontractor" is a self-employed driver.
    const want = contactKind === "sub" ? "customer" : "subcontractor";
    return (contacts || []).filter(ct => ct.type === want && ct.is_active !== false);
  }, [contacts, contactKind]);

  const downloadHref = useMemo(() => {
    const params = new URLSearchParams({
      period, date_from: dateFrom, date_to: dateTo,
      include_subs: includeSubs ? "1" : "0",
    });
    if (clientId && clientId !== "__all") params.set("client_id", clientId);
    if (siteId && siteId !== "__all") params.set("site_id", siteId);
    return fileUrl(`/reports/jobs.pdf?${params.toString()}`);
  }, [period, dateFrom, dateTo, clientId, siteId, includeSubs]);

  const contactHref = useMemo(() => {
    if (!contactId) return null;
    const p = new URLSearchParams({
      contact_id: contactId, period: cPeriod,
      date_from: cRange.from, date_to: cRange.to,
      include_clients: includeClients ? "1" : "0",
    });
    if (covClientId && covClientId !== "__all") p.set("client_id", covClientId);
    return fileUrl(`/reports/contact-coverage.pdf?${p.toString()}`);
  }, [contactId, cPeriod, cRange, includeClients, covClientId]);

  // KPI range + download href
  const kRange = useMemo(() => {
    if (kPeriod === "custom") {
      const from = kCustomFrom || kCustomTo || today();
      const to   = kCustomTo   || kCustomFrom || today();
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      return { from: lo, to: hi, label: `${lo} → ${hi}` };
    }
    if (kPeriod === "daily") return { from: kDate, to: kDate, label: kDate };
    if (kPeriod === "weekly") {
      const m = startOfWeekMon(kDate); const s = addDays(m, 6);
      return { from: m, to: s, label: `${m} → ${s}` };
    }
    const m = startOfMonth(kDate); const e = endOfMonth(kDate);
    return { from: m, to: e, label: `${m} → ${e}` };
  }, [kPeriod, kDate, kCustomFrom, kCustomTo]);

  const kpiHref = useMemo(() => {
    if (!kContactId) return null;
    const p = new URLSearchParams({
      contact_id: kContactId, period: kPeriod,
      date_from: kRange.from, date_to: kRange.to,
    });
    return fileUrl(`/reports/contact-kpi.pdf?${p.toString()}`);
  }, [kContactId, kPeriod, kRange]);

  // Ranking PDF (all subs/drivers for the same window). Uses the KPI card's
  // period pickers; the "kind" filter lets the user download either type or both.
  const [rankKind, setRankKind] = useState("both");
  const [rankSort, setRankSort] = useState("on_time");
  const rankingHref = useMemo(() => {
    const p = new URLSearchParams({
      date_from: kRange.from, date_to: kRange.to,
      period: kPeriod, kind: rankKind, sort: rankSort,
    });
    return fileUrl(`/reports/contacts-kpi-ranking.pdf?${p.toString()}`);
  }, [kRange, kPeriod, rankKind, rankSort]);

  const kpiContacts = useMemo(() => {
    const want = kContactKind === "sub" ? "customer" : "subcontractor";
    return (contacts || []).filter(ct => ct.type === want && ct.is_active !== false);
  }, [contacts, kContactKind]);

  return (
    <div data-testid="report-jobs-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Reporting · Jobs</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Job reports</h1>
        <p className="text-sm text-slate-600 mt-1">Daily, weekly or monthly PDF reports — filter by client and download a printable summary. DSP jobs include every site + driver count per day.</p>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <Label className="text-xs text-slate-600">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="rep-period" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly (Mon–Sun)</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period === "custom" ? (
            <>
              <div>
                <Label className="text-xs text-slate-600">From date</Label>
                <Input
                  data-testid="rep-date-from"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="mt-1 rounded-sm border-slate-300"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">To date</Label>
                <Input
                  data-testid="rep-date-to"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="mt-1 rounded-sm border-slate-300"
                />
              </div>
            </>
          ) : (
            <div>
              <Label className="text-xs text-slate-600">
                {period === "monthly" ? "Month" : period === "weekly" ? "Pick any day in the week" : "Date"}
              </Label>
              <Input
                data-testid="rep-date"
                type={period === "monthly" ? "month" : "date"}
                value={period === "monthly" ? (date || "").slice(0, 7) : date}
                onChange={(e) => setDate(period === "monthly" ? `${e.target.value}-01` : e.target.value)}
                className="mt-1 rounded-sm border-slate-300"
              />
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-600">Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="rep-client" className="mt-1 rounded-sm border-slate-300">
                <SelectValue placeholder="All clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All clients</SelectItem>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Site</Label>
            <Select
              value={siteId}
              onValueChange={setSiteId}
              disabled={!clientId || clientId === "__all"}
            >
              <SelectTrigger data-testid="rep-site" className="mt-1 rounded-sm border-slate-300">
                <SelectValue placeholder={clientId === "__all" ? "Pick a client first" : "All sites"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All sites</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Button asChild data-testid="rep-download" className="w-full rounded-sm bg-slate-900 hover:bg-slate-800">
              <a href={downloadHref} download rel="noopener">
                <Download className="w-4 h-4 mr-1.5" /> Download PDF
              </a>
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500 inline-flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" /> Covering: <span className="font-medium tabular-nums">{label}</span>
          </div>
          <label className="inline-flex items-center gap-2.5 text-xs text-slate-700 cursor-pointer select-none" data-testid="rep-include-subs-row">
            <Switch
              checked={includeSubs}
              onCheckedChange={setIncludeSubs}
              data-testid="rep-include-subs"
            />
            <span>
              <span className="font-medium">Show subcontractors</span>
              <span className="text-slate-500 ml-1">— turn off when sending the report to clients</span>
            </span>
          </label>
        </div>
      </Card>

      {/* Subcontractor / Self-employed coverage report */}
      <div className="pt-2">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Reporting · Coverage</div>
        <h2 className="font-display text-2xl tracking-tight font-semibold text-slate-900 mt-2">Subcontractor & driver coverage</h2>
        <p className="text-sm text-slate-600 mt-1">Pick a subcontractor or self-employed driver and download a daily, weekly or monthly PDF showing exactly which clients and sites they covered.</p>
      </div>

      <Card data-testid="contact-coverage-card" className="rounded-sm border border-slate-200 shadow-none p-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
          <div>
            <Label className="text-xs text-slate-600">Resource type</Label>
            <Select value={contactKind} onValueChange={(v) => { setContactKind(v); setContactId(""); }}>
              <SelectTrigger data-testid="cov-kind" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sub">Subcontractor</SelectItem>
                <SelectItem value="self">Self-employed driver</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">{contactKind === "sub" ? "Subcontractor" : "Driver"}</Label>
            <Select value={contactId} onValueChange={setContactId}>
              <SelectTrigger data-testid="cov-contact" className="mt-1 rounded-sm border-slate-300">
                <SelectValue placeholder={`Pick a ${contactKind === "sub" ? "subcontractor" : "driver"}`} />
              </SelectTrigger>
              <SelectContent>
                {filteredContacts.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>)}
                {filteredContacts.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">No active {contactKind === "sub" ? "subcontractors" : "drivers"}</div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Client</Label>
            <ClientCombobox
              value={covClientId}
              clients={clients}
              onChange={setCovClientId}
              testid="cov-client"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Period</Label>
            <Select value={cPeriod} onValueChange={setCPeriod}>
              <SelectTrigger data-testid="cov-period" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly (Mon–Sun)</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {cPeriod === "custom" ? (
            <>
              <div>
                <Label className="text-xs text-slate-600">From date</Label>
                <Input
                  data-testid="cov-date-from"
                  type="date"
                  value={cCustomFrom}
                  onChange={(e) => setCCustomFrom(e.target.value)}
                  className="mt-1 rounded-sm border-slate-300"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">To date</Label>
                <Input
                  data-testid="cov-date-to"
                  type="date"
                  value={cCustomTo}
                  onChange={(e) => setCCustomTo(e.target.value)}
                  className="mt-1 rounded-sm border-slate-300"
                />
              </div>
            </>
          ) : (
            <div>
              <Label className="text-xs text-slate-600">
                {cPeriod === "monthly" ? "Month" : cPeriod === "weekly" ? "Pick any day in the week" : "Date"}
              </Label>
              <Input
                data-testid="cov-date"
                type={cPeriod === "monthly" ? "month" : "date"}
                value={cPeriod === "monthly" ? (cDate || "").slice(0, 7) : cDate}
                onChange={(e) => setCDate(cPeriod === "monthly" ? `${e.target.value}-01` : e.target.value)}
                className="mt-1 rounded-sm border-slate-300"
              />
            </div>
          )}
          <div>
            <Button
              asChild={!!contactId}
              disabled={!contactId}
              data-testid="cov-download"
              className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300"
            >
              {contactId ? (
                <a href={contactHref} download rel="noopener">
                  <Download className="w-4 h-4 mr-1.5" /> Download PDF
                </a>
              ) : (
                <span><Users className="w-4 h-4 mr-1.5 inline" /> Pick one first</span>
              )}
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-500 inline-flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" /> Covering: <span className="font-medium tabular-nums">{cRange.label}</span>
          </div>
          <label className="inline-flex items-center gap-2.5 text-xs text-slate-700 cursor-pointer select-none" data-testid="cov-include-clients-row">
            <Switch
              checked={includeClients}
              onCheckedChange={setIncludeClients}
              data-testid="cov-include-clients"
            />
            <span>
              <span className="font-medium">Show client</span>
              <span className="text-slate-500 ml-1">— turn off when sending the report to a subcontractor</span>
            </span>
          </label>
        </div>
      </Card>

      {/* Subcontractor / Self-employed KPI report */}
      <div className="pt-2">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Reporting · KPI</div>
        <h2 className="font-display text-2xl tracking-tight font-semibold text-slate-900 mt-2">Contact KPI report</h2>
        <p className="text-sm text-slate-600 mt-1">One-pager PDF for a subcontractor or self-employed driver: jobs worked, deductions logged, £ deducted, and an on-time percentage over any custom window.</p>
      </div>

      <Card data-testid="contact-kpi-card" className="rounded-sm border border-slate-200 shadow-none p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <Label className="text-xs text-slate-600">Resource type</Label>
            <Select value={kContactKind} onValueChange={(v) => { setKContactKind(v); setKContactId(""); }}>
              <SelectTrigger data-testid="kpi-kind" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sub">Subcontractor</SelectItem>
                <SelectItem value="self">Self-employed driver</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">{kContactKind === "sub" ? "Subcontractor" : "Driver"}</Label>
            <Select value={kContactId} onValueChange={setKContactId}>
              <SelectTrigger data-testid="kpi-contact" className="mt-1 rounded-sm border-slate-300">
                <SelectValue placeholder={`Pick a ${kContactKind === "sub" ? "subcontractor" : "driver"}`} />
              </SelectTrigger>
              <SelectContent>
                {kpiContacts.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>)}
                {kpiContacts.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">No active {kContactKind === "sub" ? "subcontractors" : "drivers"}</div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Period</Label>
            <Select value={kPeriod} onValueChange={setKPeriod}>
              <SelectTrigger data-testid="kpi-period" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly (Mon–Sun)</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {kPeriod === "custom" ? (
            <>
              <div>
                <Label className="text-xs text-slate-600">From date</Label>
                <Input
                  data-testid="kpi-date-from"
                  type="date"
                  value={kCustomFrom}
                  onChange={(e) => setKCustomFrom(e.target.value)}
                  className="mt-1 rounded-sm border-slate-300"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">To date</Label>
                <Input
                  data-testid="kpi-date-to"
                  type="date"
                  value={kCustomTo}
                  onChange={(e) => setKCustomTo(e.target.value)}
                  className="mt-1 rounded-sm border-slate-300"
                />
              </div>
            </>
          ) : (
            <div>
              <Label className="text-xs text-slate-600">
                {kPeriod === "monthly" ? "Month" : kPeriod === "weekly" ? "Pick any day in the week" : "Date"}
              </Label>
              <Input
                data-testid="kpi-date"
                type={kPeriod === "monthly" ? "month" : "date"}
                value={kPeriod === "monthly" ? (kDate || "").slice(0, 7) : kDate}
                onChange={(e) => setKDate(kPeriod === "monthly" ? `${e.target.value}-01` : e.target.value)}
                className="mt-1 rounded-sm border-slate-300"
              />
            </div>
          )}
          <div className={kPeriod === "custom" ? "md:col-span-5" : ""}>
            <Button
              asChild={!!kContactId}
              disabled={!kContactId}
              data-testid="kpi-download"
              className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300"
            >
              {kContactId ? (
                <a href={kpiHref} download rel="noopener">
                  <Download className="w-4 h-4 mr-1.5" /> Download KPI PDF
                </a>
              ) : (
                <span><Users className="w-4 h-4 mr-1.5 inline" /> Pick one first</span>
              )}
            </Button>
          </div>
        </div>
        <div className="mt-4 text-xs text-slate-500 inline-flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" /> Covering: <span className="font-medium tabular-nums">{kRange.label}</span>
        </div>

        {/* ---- All-contacts KPI ranking (shares the KPI period pickers) ---- */}
        <div className="mt-6 pt-5 border-t border-slate-200">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Compare all subs & drivers</div>
          <p className="text-sm text-slate-600 mt-1.5">
            Single PDF ranking every contact by on-time % and £ deducted for the same window above.
          </p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="text-xs text-slate-600">Include</Label>
              <Select value={rankKind} onValueChange={setRankKind}>
                <SelectTrigger data-testid="rank-kind" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">All subs & drivers</SelectItem>
                  <SelectItem value="sub">Subcontractors only</SelectItem>
                  <SelectItem value="self">Self-employed only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Sort by</Label>
              <Select value={rankSort} onValueChange={setRankSort}>
                <SelectTrigger data-testid="rank-sort" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_time">On-time % (best first)</SelectItem>
                  <SelectItem value="deductions_amount">£ Deducted (highest first)</SelectItem>
                  <SelectItem value="jobs">Jobs worked (most first)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Button asChild data-testid="rank-download" className="w-full rounded-sm bg-slate-900 hover:bg-slate-800">
                <a href={rankingHref} download rel="noopener">
                  <Download className="w-4 h-4 mr-1.5" /> Download ranking PDF
                </a>
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}


// Searchable client picker matching the Shadcn Select chrome so it sits neatly
// next to the other filters. "__all" value means "include every client".
function ClientCombobox({ value, onChange, clients, testid }) {
  const [open, setOpen] = useState(false);
  const isAll = !value || value === "__all";
  const selected = clients.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="mt-1 flex h-9 w-full items-center justify-between rounded-sm border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
        >
          <span className="truncate text-left">
            {isAll ? "All clients" : (selected?.name || "Choose client")}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 rounded-sm" align="start">
        <Command>
          <CommandInput placeholder="Type to search clients..." className="h-9" />
          <CommandList>
            <CommandEmpty>No clients match.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all-clients"
                onSelect={() => { onChange("__all"); setOpen(false); }}
              >
                <Check className={`mr-2 h-4 w-4 ${isAll ? "opacity-100" : "opacity-0"}`} />
                <span className="font-medium">All clients</span>
              </CommandItem>
              {clients.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.name}
                  onSelect={() => { onChange(c.id); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
